import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Music, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  const orderId = searchParams.get("order_id");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  // Verify payment with Stripe session
  useEffect(() => {
    if (!user || !orderId || !sessionId) {
      setVerifying(false);
      return;
    }

    const verify = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("verify-payment", {
          body: { sessionId, orderId },
        });
        if (error) {
          console.error("Payment verification error:", error);
        } else if (data?.success) {
          setVerified(true);
        } else {
          console.error("Payment verification failed:", data);
        }
      } catch (err) {
        console.error("Payment verification error:", err);
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [user, orderId, sessionId]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-xl text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card rounded-2xl p-8 md:p-12 shadow-soft"
          >
            {verifying && orderId ? (
              <>
                <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
                <h1 className="font-heading text-3xl font-bold text-foreground mb-3">
                  Confirming Payment...
                </h1>
                <p className="text-muted-foreground mb-8">
                  Please wait while we verify your payment with Stripe.
                </p>
              </>
            ) : (
              <>
                <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
                <h1 className="font-heading text-3xl font-bold text-foreground mb-3">
                  Payment Successful!
                </h1>
                <p className="text-muted-foreground mb-8">
                  Your order has been submitted and payment confirmed. We'll start working on your personalized song right away!
                </p>
                <Button
                  variant="hero"
                  size="lg"
                  onClick={() => navigate(orderId ? `/order/${orderId}` : "/dashboard")}
                >
                  <Music className="w-5 h-5 mr-2" />
                  {orderId ? "View Your Order" : "Go to Dashboard"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutSuccess;
